import { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useToast } from "~/hooks/use-toast";
import logger from "~/utils/logger";
import { useTextSnipMutation } from "./useTextSnipMutation";

type NormalizedRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Point = { x: number; y: number };

type StoredBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type TextSnipState =
  | { status: "idle" }
  | {
      status: "awaitingSecondCorner";
      startX: number;
      startY: number;
      currentX: number;
      currentY: number;
    }
  | {
      status: "freehandDrawing";
      points: Point[];
    }
  | { status: "processing"; rect: NormalizedRect; points?: Point[] }
  | { status: "copying"; rect: NormalizedRect; text: string; points?: Point[] }
  | { status: "success"; text: string }
  | { status: "error"; rect: NormalizedRect; message: string; points?: Point[] }
  | { status: "empty"; rect: NormalizedRect; points?: Point[] };

const MIN_RECT_SIZE = 20;
const SUCCESS_FADE_DELAY_MS = 300;
const PREVIEW_MAX_LENGTH = 80;
const DRAG_THRESHOLD_PX = 5;
const FREEHAND_MIN_POINT_DISTANCE = 0.005;

type SnipCompositionParams = {
  pageId: string;
  organizationId: string;
  projectId: string;
  rotation: number;
};

type UseTextSnipReturn = {
  snipState: TextSnipState;
  isSnipActive: boolean;
  startSnip: () => void;
  cancelSnip: () => void;
  handlers: {
    onMouseDown: (
      e: React.MouseEvent,
      containerRect: DOMRect,
      imgRect: DOMRect
    ) => void;
    onMouseMove: (e: React.MouseEvent, containerRect: DOMRect) => void;
    onMouseUp: () => void;
    onHoverMove: (e: React.MouseEvent, containerRect: DOMRect) => void;
  };
};

function normalizeRect(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number
): NormalizedRect {
  const x = Math.min(startX, currentX);
  const y = Math.min(startY, currentY);
  const width = Math.abs(currentX - startX);
  const height = Math.abs(currentY - startY);
  return { x, y, width, height };
}

function boundingRectFromPoints(points: Point[]): NormalizedRect {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function containerToImageRect(
  rect: NormalizedRect,
  containerBounds: StoredBounds | null,
  imageBounds: StoredBounds | null
): NormalizedRect {
  if (
    !containerBounds ||
    !imageBounds ||
    imageBounds.width === 0 ||
    imageBounds.height === 0
  ) {
    return rect;
  }

  const imgOffsetX = imageBounds.left - containerBounds.left;
  const imgOffsetY = imageBounds.top - containerBounds.top;

  const pixelX = rect.x * containerBounds.width;
  const pixelY = rect.y * containerBounds.height;
  const pixelW = rect.width * containerBounds.width;
  const pixelH = rect.height * containerBounds.height;

  const imgX = (pixelX - imgOffsetX) / imageBounds.width;
  const imgY = (pixelY - imgOffsetY) / imageBounds.height;
  const imgW = pixelW / imageBounds.width;
  const imgH = pixelH / imageBounds.height;

  const clampedX = Math.max(0, Math.min(1, imgX));
  const clampedY = Math.max(0, Math.min(1, imgY));

  return {
    x: clampedX,
    y: clampedY,
    width: Math.max(0, Math.min(1, imgX + imgW) - clampedX),
    height: Math.max(0, Math.min(1, imgY + imgH) - clampedY),
  };
}

function inverseRotateRect(
  rect: NormalizedRect,
  rotation: number
): NormalizedRect {
  const normalized = ((rotation % 360) + 360) % 360;
  switch (normalized) {
    case 90:
      return {
        x: rect.y,
        y: 1 - rect.x - rect.width,
        width: rect.height,
        height: rect.width,
      };
    case 180:
      return {
        x: 1 - rect.x - rect.width,
        y: 1 - rect.y - rect.height,
        width: rect.width,
        height: rect.height,
      };
    case 270:
      return {
        x: 1 - rect.y - rect.height,
        y: rect.x,
        width: rect.height,
        height: rect.width,
      };
    default:
      return rect;
  }
}

function containerToImagePoints(
  points: Point[],
  containerBounds: StoredBounds | null,
  imageBounds: StoredBounds | null
): Point[] {
  if (
    !containerBounds ||
    !imageBounds ||
    imageBounds.width === 0 ||
    imageBounds.height === 0
  ) {
    return points;
  }

  const imgOffsetX = imageBounds.left - containerBounds.left;
  const imgOffsetY = imageBounds.top - containerBounds.top;

  return points.map((p) => {
    const pixelX = p.x * containerBounds.width;
    const pixelY = p.y * containerBounds.height;
    return {
      x: Math.max(0, Math.min(1, (pixelX - imgOffsetX) / imageBounds.width)),
      y: Math.max(0, Math.min(1, (pixelY - imgOffsetY) / imageBounds.height)),
    };
  });
}

function inverseRotatePoint(point: Point, rotation: number): Point {
  const normalized = ((rotation % 360) + 360) % 360;
  switch (normalized) {
    case 90:
      return { x: point.y, y: 1 - point.x };
    case 180:
      return { x: 1 - point.x, y: 1 - point.y };
    case 270:
      return { x: 1 - point.y, y: point.x };
    default:
      return point;
  }
}

export function useTextSnip(
  params: SnipCompositionParams | null
): UseTextSnipReturn {
  const [snipState, setSnipState] = useState<TextSnipState>({
    status: "idle",
  });
  const snipStateRef = useRef<TextSnipState>(snipState);
  const { toast } = useToast();
  const intl = useIntl();
  const mutation = useTextSnipMutation();
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;
  const containerSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const containerBoundsRef = useRef<StoredBounds | null>(null);
  const imageBoundsRef = useRef<StoredBounds | null>(null);

  const dragStartClientRef = useRef<Point>({ x: 0, y: 0 });
  const dragStartNormRef = useRef<Point>({ x: 0, y: 0 });
  const isPendingRef = useRef(false);
  const isDraggingRef = useRef(false);

  const isSnipActive = snipState.status !== "idle";

  const startSnip = useCallback(() => {
    snipStateRef.current = { status: "idle" };
    setSnipState({ status: "idle" });
  }, []);

  const cancelSnip = useCallback(() => {
    mutationRef.current.reset();
    isPendingRef.current = false;
    isDraggingRef.current = false;
    snipStateRef.current = { status: "idle" };
    setSnipState({ status: "idle" });
  }, []);

  const processSnip = useCallback(
    async (rect: NormalizedRect, freehandPoints?: Point[]) => {
      if (!params) return;

      const processingState: TextSnipState = {
        status: "processing",
        rect,
        points: freehandPoints,
      };
      snipStateRef.current = processingState;
      setSnipState(processingState);

      try {
        const adjustedRect = containerToImageRect(
          rect,
          containerBoundsRef.current,
          imageBoundsRef.current
        );

        const imageRect = inverseRotateRect(adjustedRect, params.rotation);

        let imagePolygon: Point[] | undefined;
        if (freehandPoints && freehandPoints.length >= 3) {
          const adjustedPoints = containerToImagePoints(
            freehandPoints,
            containerBoundsRef.current,
            imageBoundsRef.current
          );
          imagePolygon = adjustedPoints.map((p) =>
            inverseRotatePoint(p, params.rotation)
          );
        }

        const result = await mutation.mutateAsync({
          organizationId: params.organizationId,
          projectId: params.projectId,
          pageId: params.pageId,
          rect: imageRect,
          polygon: imagePolygon,
        });

        const extractedText = result.text.trim();

        if (!extractedText) {
          const emptyState: TextSnipState = {
            status: "empty",
            rect,
            points: freehandPoints,
          };
          snipStateRef.current = emptyState;
          setSnipState(emptyState);
          toast({
            title: intl.formatMessage({
              id: "docViewer.textSnip.empty.title",
            }),
            description: intl.formatMessage({
              id: "docViewer.textSnip.empty.description",
            }),
          });
          setTimeout(() => {
            if (snipStateRef.current.status !== "empty") return;
            const idle: TextSnipState = { status: "idle" };
            snipStateRef.current = idle;
            setSnipState(idle);
          }, 2000);
          return;
        }

        const copyingState: TextSnipState = {
          status: "copying",
          rect,
          text: extractedText,
          points: freehandPoints,
        };
        snipStateRef.current = copyingState;
        setSnipState(copyingState);

        try {
          await navigator.clipboard.writeText(extractedText);
        } catch {
          const textarea = document.createElement("textarea");
          textarea.value = extractedText;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        const successState: TextSnipState = {
          status: "success",
          text: extractedText,
        };
        snipStateRef.current = successState;
        setSnipState(successState);

        const preview =
          extractedText.length > PREVIEW_MAX_LENGTH
            ? extractedText.substring(0, PREVIEW_MAX_LENGTH) + "..."
            : extractedText;

        toast({
          title: intl.formatMessage({
            id: "docViewer.textSnip.success.title",
          }),
          description: intl.formatMessage(
            { id: "docViewer.textSnip.success.description" },
            { preview }
          ),
        });

        setTimeout(() => {
          if (snipStateRef.current.status !== "success") return;
          const idle: TextSnipState = { status: "idle" };
          snipStateRef.current = idle;
          setSnipState(idle);
        }, SUCCESS_FADE_DELAY_MS);
      } catch (error) {
        logger.error("Text snip failed", { error });
        const errorState: TextSnipState = {
          status: "error",
          rect,
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
          points: freehandPoints,
        };
        snipStateRef.current = errorState;
        setSnipState(errorState);
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "docViewer.textSnip.error.title",
          }),
          description: intl.formatMessage({
            id: "docViewer.textSnip.error.description",
          }),
        });
        setTimeout(() => {
          if (snipStateRef.current.status !== "error") return;
          const idle: TextSnipState = { status: "idle" };
          snipStateRef.current = idle;
          setSnipState(idle);
        }, 3000);
      }
    },
    [params, mutation, toast, intl]
  );

  const storeBounds = useCallback(
    (containerRect: DOMRect, imgRect: DOMRect) => {
      containerSizeRef.current = {
        width: containerRect.width,
        height: containerRect.height,
      };
      containerBoundsRef.current = {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height,
      };
      imageBoundsRef.current = {
        left: imgRect.left,
        top: imgRect.top,
        width: imgRect.width,
        height: imgRect.height,
      };
    },
    []
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent, containerRect: DOMRect, imgRect: DOMRect) => {
      const currentStatus = snipStateRef.current.status;

      if (
        currentStatus !== "idle" &&
        currentStatus !== "awaitingSecondCorner" &&
        currentStatus !== "error" &&
        currentStatus !== "empty"
      ) {
        return;
      }

      storeBounds(containerRect, imgRect);

      const normX = Math.max(
        0,
        Math.min(1, (e.clientX - containerRect.left) / containerRect.width)
      );
      const normY = Math.max(
        0,
        Math.min(1, (e.clientY - containerRect.top) / containerRect.height)
      );

      dragStartClientRef.current = { x: e.clientX, y: e.clientY };
      dragStartNormRef.current = { x: normX, y: normY };
      isPendingRef.current = true;
      isDraggingRef.current = false;

      if (currentStatus === "awaitingSecondCorner") {
        const prev = snipStateRef.current;
        if (prev.status === "awaitingSecondCorner") {
          const newState: TextSnipState = {
            ...prev,
            currentX: normX,
            currentY: normY,
          };
          snipStateRef.current = newState;
          setSnipState(newState);
        }
      }
    },
    [storeBounds]
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent, containerRect: DOMRect) => {
      if (!isPendingRef.current && !isDraggingRef.current) return;

      const normX = Math.max(
        0,
        Math.min(1, (e.clientX - containerRect.left) / containerRect.width)
      );
      const normY = Math.max(
        0,
        Math.min(1, (e.clientY - containerRect.top) / containerRect.height)
      );

      if (isPendingRef.current) {
        const dx = e.clientX - dragStartClientRef.current.x;
        const dy = e.clientY - dragStartClientRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > DRAG_THRESHOLD_PX) {
          isPendingRef.current = false;
          isDraggingRef.current = true;

          const startPoint = dragStartNormRef.current;
          const newState: TextSnipState = {
            status: "freehandDrawing",
            points: [
              { x: startPoint.x, y: startPoint.y },
              { x: normX, y: normY },
            ],
          };
          snipStateRef.current = newState;
          setSnipState(newState);
        }
        return;
      }

      if (isDraggingRef.current) {
        setSnipState((prev) => {
          if (prev.status !== "freehandDrawing") return prev;
          const lastPoint = prev.points[prev.points.length - 1];
          const pointDx = normX - lastPoint.x;
          const pointDy = normY - lastPoint.y;
          if (
            Math.sqrt(pointDx * pointDx + pointDy * pointDy) <
            FREEHAND_MIN_POINT_DISTANCE
          ) {
            return prev;
          }
          const updated: TextSnipState = {
            status: "freehandDrawing",
            points: [...prev.points, { x: normX, y: normY }],
          };
          snipStateRef.current = updated;
          return updated;
        });
      }
    },
    []
  );

  const onMouseUp = useCallback(() => {
    const current = snipStateRef.current;

    if (isPendingRef.current) {
      isPendingRef.current = false;
      isDraggingRef.current = false;

      if (current.status === "awaitingSecondCorner") {
        const normX = dragStartNormRef.current.x;
        const normY = dragStartNormRef.current.y;
        const rect = normalizeRect(
          current.startX,
          current.startY,
          normX,
          normY
        );

        const { width: containerWidth, height: containerHeight } =
          containerSizeRef.current;
        const rectWidthPx = rect.width * containerWidth;
        const rectHeightPx = rect.height * containerHeight;

        if (rectWidthPx < MIN_RECT_SIZE || rectHeightPx < MIN_RECT_SIZE) {
          snipStateRef.current = { status: "idle" };
          setSnipState({ status: "idle" });
          return;
        }

        processSnip(rect);
        return;
      }

      const normX = dragStartNormRef.current.x;
      const normY = dragStartNormRef.current.y;
      const newState: TextSnipState = {
        status: "awaitingSecondCorner",
        startX: normX,
        startY: normY,
        currentX: normX,
        currentY: normY,
      };
      snipStateRef.current = newState;
      setSnipState(newState);
      return;
    }

    if (isDraggingRef.current) {
      isDraggingRef.current = false;

      if (current.status === "freehandDrawing" && current.points.length >= 3) {
        const closedPoints = [...current.points, current.points[0]];
        const rect = boundingRectFromPoints(closedPoints);

        const { width: containerWidth, height: containerHeight } =
          containerSizeRef.current;
        const rectWidthPx = rect.width * containerWidth;
        const rectHeightPx = rect.height * containerHeight;

        if (rectWidthPx < MIN_RECT_SIZE || rectHeightPx < MIN_RECT_SIZE) {
          snipStateRef.current = { status: "idle" };
          setSnipState({ status: "idle" });
          return;
        }

        processSnip(rect, closedPoints);
        return;
      }

      snipStateRef.current = { status: "idle" };
      setSnipState({ status: "idle" });
    }
  }, [processSnip]);

  const onHoverMove = useCallback(
    (e: React.MouseEvent, containerRect: DOMRect) => {
      if (snipStateRef.current.status !== "awaitingSecondCorner") return;

      const normX = Math.max(
        0,
        Math.min(1, (e.clientX - containerRect.left) / containerRect.width)
      );
      const normY = Math.max(
        0,
        Math.min(1, (e.clientY - containerRect.top) / containerRect.height)
      );

      setSnipState((prev) => {
        if (prev.status !== "awaitingSecondCorner") return prev;
        const updated: TextSnipState = {
          ...prev,
          currentX: normX,
          currentY: normY,
        };
        snipStateRef.current = updated;
        return updated;
      });
    },
    []
  );

  return {
    snipState,
    isSnipActive,
    startSnip,
    cancelSnip,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onHoverMove,
    },
  };
}

export type { NormalizedRect, Point, TextSnipState };
